import React from 'react';

/**
 * TierRestrictedPagination Component
 * 
 * Provides consistent pagination across all pages with tier restrictions.
 * Free users are limited to page 1 only.
 * 
 * @param {Object} props
 * @param {number} props.currentPage - Current page number
 * @param {number} props.totalPages - Total number of pages
 * @param {function} props.onPageChange - Callback when page changes
 * @param {boolean} props.isLoading - Whether data is loading
 * @param {boolean} props.disabled - Whether pagination is disabled
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Additional content (e.g., stats, download button)
 */
const TierRestrictedPagination = ({
    currentPage = 1,
    totalPages = 1,
    onPageChange,
    isLoading = false,
    disabled = false,
    className = '',
    children
}) => {
    // Get user tier for restrictions
    const userTier = window.userTier || 'free';
    const isProfessionalOrHigher = ['professional', 'enterprise'].includes(userTier);

    // Handle page change with tier restrictions
    const handlePageChange = (newPage) => {
        // Restrict free users to page 1 only
        if (!isProfessionalOrHigher && newPage > 1) {
            return; // Silently prevent navigation for free users
        }

        if (newPage >= 1 && newPage <= totalPages && !isLoading && !disabled) {
            onPageChange(newPage);
        }
    };

    // Don't render if only one page
    if (totalPages <= 1) {
        return null;
    }

    const canGoPrevious = currentPage > 1 && !isLoading && !disabled;
    const canGoNext = currentPage < totalPages && !isLoading && !disabled;

    return (
        <div className={`pagination-container ${className}`}>
            <div className="pagination">
                {children && (
                    <div className="pagination-stats">
                        {children}
                    </div>
                )}
                
                <div className="pagination-controls">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={!canGoPrevious}
                        className="pagination-btn"
                        aria-label="Go to previous page"
                    >
                        Previous
                    </button>
                    
                    <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                    </span>
                    
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={!canGoNext || (!isProfessionalOrHigher && currentPage >= 1)}
                        className="pagination-btn"
                        aria-label={!isProfessionalOrHigher && currentPage >= 1 ? "Professional+ required for next page" : "Go to next page"}
                        title={!isProfessionalOrHigher && currentPage >= 1 ? "Professional+ required for additional pages" : ""}
                    >
                        Next
                    </button>
                </div>
                
                {/* Tier restriction message below pagination */}
                {!isProfessionalOrHigher && currentPage >= 1 && (
                    <div className="tier-restriction-below">
                        <span className="professional-gradient">Professional+</span> required for additional pages
                    </div>
                )}
            </div>
        </div>
    );
};

export default TierRestrictedPagination;
